import LocationInterface from '@solvhealth/types/interfaces/Location';
import { useMemo } from 'react';
import { getProfileFromBooking } from '~/core/util/account';
import { EMPTY_STRING } from '~/core/util/string';
import { PaperworkState } from '~/ducks/paperwork';
import { useSolvSelector } from '~/reducers';
import { SectionNumber, SECTIONS } from '../constants';
import { SectionInfo, SectionNumberObj } from '../interfaces';
import { PGSectionNumber, PG_SECTIONS } from '../providerGroups/constants';

/**
 * Sections that are numbered in the progress bar i.e. the Patient section is 1 / 6
 * Verification & Review and Consent are not present because they are included
 * in the Finalize 6 / 6 section
 */
const NUMBERED_SECTIONS = [
  SectionNumber.Patient,
  SectionNumber.Employer,
  SectionNumber.HealthcareContacts,
  SectionNumber.Payment,
  SectionNumber.Custom,
  SectionNumber.Finalize,
];

/**
 * Mapping of SectionNumbers to the overall section they're a part of
 * (when thinking of numbering sections)
 * Example: Verification & Review, Consent, and Finalize are all separate
 * sections but all should show up under the same Section _ of _
 */
const MAP_SECTION_TO_CONTAINING_SECTION = new Map([
  [SectionNumber.Consent, SectionNumber.Finalize],
  [SectionNumber.VerificationAndReview, SectionNumber.Finalize],
]);

const PG_NUMBERED_SECTIONS = [
  PGSectionNumber.Patient,
  PGSectionNumber.Medical,
  PGSectionNumber.LifestyleFactors,
  PGSectionNumber.HealthcareContacts,
  PGSectionNumber.VerificationAndReview,
];

export const useGetSectionsProgress = <Sect extends SectionNumber | PGSectionNumber>(
  paperwork: PaperworkState,
  sectionValue: Sect,
  section: SectionNumberObj<Sect>,
  bookingId: string = EMPTY_STRING
) => {
  const location = useSolvSelector((state) => state.location as LocationInterface);
  const booking = useSolvSelector((state) => state.pastBookings?.[bookingId]);
  const accountSummary = useSolvSelector((state) => state.account.summary);
  const sections: Map<SectionNumber | PGSectionNumber, SectionInfo> =
    section === PGSectionNumber ? PG_SECTIONS : SECTIONS;
  const numberedSections: (SectionNumber | PGSectionNumber)[] =
    section === PGSectionNumber ? PG_NUMBERED_SECTIONS : NUMBERED_SECTIONS;
  const validSections = useMemo(() => {
    const userProfile = getProfileFromBooking(accountSummary, booking);
    return new Map(
      [...sections].filter(
        ([k, v]) =>
          numberedSections.includes(k) &&
          (!v.dependencyFn || v.dependencyFn({ location, booking, userProfile, paperwork }))
      )
    );
  }, [location, booking, paperwork, sections, numberedSections, accountSummary]);

  const totalSections = validSections.size;
  let useSectionValue = sectionValue;
  if (
    section === SectionNumber &&
    MAP_SECTION_TO_CONTAINING_SECTION.has(sectionValue as SectionNumber)
  ) {
    useSectionValue = MAP_SECTION_TO_CONTAINING_SECTION.get(sectionValue as SectionNumber)! as Sect;
  }
  const sectionNumber = [...validSections].findIndex(([k, v]) => k === useSectionValue);

  return {
    sectionNumber: sectionNumber + 1,
    totalSections,
  };
};
